import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';
import React, { useEffect, useRef, useState } from 'react';
import Eyebrow from '../Eyebrow';
import { Box, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Scroll-pinned "windmill" diagram: as the visitor scrolls through this section, a 4-blade
 * mill turns and alternately reveals the Certificates agent panel, then the Accounting agent
 * panel. Progress follows the section's bounding rect with bounded desktop transitions
 * and frame-rate-independent damping on narrow screens, keeping the mill and panels synchronized.
 * Phones and foldables place the mill above compact panels and retain native touch scrolling.
 * Reduced-motion visitors get static stacked offers.
 */

const clamp = (v: number) => Math.max(0, Math.min(1, v));
const ease = (t: number) => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const BLADE_COLORS = [V.brandBlue, V.brandCyan, V.brandViolet, V.brandTeal];
const INFINITY_PATH = 'M 220 50 C 160 -8 18 -8 18 50 C 18 108 160 108 220 50 C 280 -8 422 -8 422 50 C 422 108 280 108 220 50';
const INFINITY_DOTS = 216;
const INFINITY_DURATION = 44;
const CERTIFICATES_STOP = 0;
const ACCOUNTING_STOP = 0.74;
const DESKTOP_TRANSITION_MS = 900;

const offers = {
  certificates: {
    pain: 'Certificates pile up. Deadlines slip. Your risk grows.',
    description: 'A checklist tells you what needs doing. MillOS acts: reads incoming certificates, tracks expiry dates and coordinates renewals with engineers and tenants - keeping your records up to date as new documents arrive.',
    compactDescription: 'Reads certificates, tracks expiry dates and coordinates renewals with engineers and tenants. New certificates are filed against the right property, keeping your records ready when you need them.',
    outcome: 'Beyond tracking. Into action.',
  },
  accounting: {
    pain: 'Rent arrives. Expenses scatter. Quarter-end becomes a scramble.',
    description: 'A spreadsheet waits for you to update it. MillOS matches rent to properties, organises incoming invoices and prepares figures for your accountant—keeping your books moving throughout the quarter.',
    compactDescription: 'Matches rent, organises expenses and prepares quarterly figures for your accountant. Incoming invoices are recorded against the right property, keeping your books organised throughout the quarter.',
    outcome: 'Beyond recording. Into preparation.',
  },
};
const offerHeading = 'AI agents for your property. Not another checklist.';

const AgentMock: React.FC<{ accounting: boolean; compact: boolean }> = ({ accounting, compact }) => {
  const rowSx = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
    background: C.white, border: `1px solid ${V.border}`, borderRadius: V.borderRadius11px,
    padding: compact ? '4px 8px' : '10px 12px',
    ...(compact ? { lineHeight: 1.2, '& > span': { fontSize: 12 }, '@media (max-height:700px)': { py: '2px' } } : {}),
  };
  const rows = accounting ? [
    ['Expenses filed', '£3,480'],
    ['Q3 figures', 'Prepared'],
    ['MTD pack', 'Sent to accountant'],
  ] : [
    ['Gas Safety · Flat 2', 'Renewed ✓'],
    ['EICR · Flat 5', 'Booked Thu'],
    ['EPC · Flat 7', 'Valid to 2031'],
    ['Portfolio score', '96 / 100'],
  ];
  const color = accounting ? V.tealStrong : V.brandBlue;
  return (
    <>
      {accounting && (
        <Box sx={{ px: compact ? 0 : 1, pt: compact ? 0 : 0.5 }}>
          <Box sx={{ fontSize: V.fontSize12, color: V.body, mb: 0.5, ...(compact ? { lineHeight: 1.2 } : {}) }}>Monthly expenses</Box>
          <Box component="svg" role="img" aria-label="Example monthly expenses: April £900, May £1,200, June £1,380" viewBox="0 0 240 86" sx={{ display: 'block', width: '100%', height: compact ? 64 : 86, ...(compact ? { '@media (max-height:700px)': { height: 44 } } : {}) }}>
            <line x1="12" y1="64" x2="228" y2="64" stroke={V.border} />
            {[{ month: 'Apr', amount: 900 }, { month: 'May', amount: 1200 }, { month: 'Jun', amount: 1380 }].map(({ month, amount }, i) => {
              const height = amount / 1380 * 46;
              const x = 34 + i * 72;
              return (
                <g key={month}>
                  <rect x={x} y={64 - height} width="28" height={height} rx="4" fill={V.tealStrong} />
                  <text x={x + 14} y={59 - height} textAnchor="middle" fill={C.navy} fontFamily={F.body} fontSize="10">£{amount.toLocaleString('en-GB')}</text>
                  <text x={x + 14} y="80" textAnchor="middle" fill={V.body} fontFamily={F.body} fontSize="10">{month}</text>
                </g>
              );
            })}
          </Box>
        </Box>
      )}
      {rows.map(([label, status], index) => {
        const last = index === rows.length - 1;
        const statusColor = accounting
          ? index === 1 ? V.tealText : C.navy
          : index === 0 ? V.success : index === 1 ? V.brandBlue : C.navy;
        return (
          <Box key={label} sx={{ ...rowSx, ...(last ? { background: color } : {}) }}>
            <Box component="span" sx={{ fontSize: V.fontSize13_5, color: last ? V.onDark : C.navy }}>{label}</Box>
            <Box component="span" sx={{ fontSize: V.fontSize12, fontWeight: V.fontWeight700, color: last ? V.onDark : statusColor }}>{status}</Box>
          </Box>
        );
      })}
    </>
  );
};

const MillosAgentsScroll: React.FC = () => {
  const outerRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const selectOfferRef = useRef<((stop: number) => void) | null>(null);
  const [p, setP] = useState(0);
  const [vh, setVh] = useState(900);
  const prefersReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isPhone = useMediaQuery('(max-width:760px)');
  const isMedium = useMediaQuery('(min-width:761px) and (max-width:1100px)');
  const isUnfolded = useMediaQuery('(min-width:640px) and (max-width:1100px)');
  const isNarrow = isPhone || isMedium;
  const stacked = prefersReduced;

  useEffect(() => {
    if (stacked) return;
    let raf: number | null = null;
    let target = 0;
    let current = 0;
    let previousTime: number | null = null;
    let transitionStart: number | null = null;
    let transitionFrom = 0;
    let snapTimer: number | null = null;
    let touching = false;
    const travelDistance = (height: number) =>
      height - (isNarrow ? stickyRef.current?.offsetHeight || window.innerHeight - 80 : window.innerHeight);
    const tick = (time: number) => {
      raf = null;
      const elapsed = previousTime === null ? 16 : Math.min(64, time - previousTime);
      previousTime = time;
      if (isNarrow) {
        current += (target - current) * (1 - Math.exp(-elapsed / 720));
        if (Math.abs(target - current) < 0.0001) current = target;
      } else {
        if (transitionStart === null) transitionStart = time;
        const progress = clamp((time - transitionStart) / DESKTOP_TRANSITION_MS);
        current = progress === 1 ? target : lerp(transitionFrom, target, ease(progress));
      }
      setP(current);
      if (current !== target) raf = requestAnimationFrame(tick);
      else previousTime = null;
    };
    const measure = () => {
      const el = outerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const topOffset = isNarrow ? 80 : 0;
      const total = travelDistance(r.height);
      const offset = topOffset - r.top;
      const progress = !isNarrow && Math.abs(offset) <= 1 ? 0
        : !isNarrow && Math.abs(offset - total) <= 1 ? 1
        : total > 0 ? clamp(offset / total) : 0;
      const nextTarget = total > 0
        ? progress * (isNarrow ? 1 : ACCOUNTING_STOP) : 0;
      if (nextTarget !== target) {
        transitionFrom = current;
        transitionStart = null;
        target = nextTarget;
      }
      setVh(window.innerHeight);
      if (!raf) raf = requestAnimationFrame(tick);
      if (snapTimer !== null) window.clearTimeout(snapTimer);
      snapTimer = null;
      if (isNarrow && !touching) {
        snapTimer = window.setTimeout(() => {
          snapTimer = null;
          const rect = el.getBoundingClientRect();
          const travel = travelDistance(rect.height);
          const progress = travel > 0 ? (80 - rect.top) / travel : 0;
          // Leave native scrolling free above the first and below the last offer.
          if (progress <= CERTIFICATES_STOP || progress >= ACCOUNTING_STOP) return;
          const stop = progress < ACCOUNTING_STOP / 2 ? CERTIFICATES_STOP : ACCOUNTING_STOP;
          if (Math.abs(progress - stop) < 0.001) return;
          window.scrollTo({ top: window.scrollY + rect.top - 80 + travel * stop, behavior: 'auto' });
          measure();
        }, 180);
      }
    };
    const onTouchStart = () => {
      touching = true;
      if (snapTimer !== null) window.clearTimeout(snapTimer);
      snapTimer = null;
    };
    const onTouchEnd = () => {
      touching = false;
      measure();
    };
    let gestureLocked = false;
    let lastWheelAt = 0;
    let lastDelta = 0;
    selectOfferRef.current = (stop) => {
      const el = outerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      gestureLocked = false;
      window.scrollTo({
        top: window.scrollY + rect.top - (isNarrow ? 80 : 0)
          + travelDistance(rect.height) * (isNarrow ? stop : stop / ACCOUNTING_STOP),
        behavior: 'auto',
      });
      measure();
    };
    const onWheel = (event: WheelEvent) => {
      if (isNarrow) return;
      if (event.ctrlKey || !event.cancelable || Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.deltaY === 0) return;
      const el = outerRef.current;
      if (!el) return;
      // Preserve scrolling within menus, dialogs and other nested scroll containers.
      let node = event.target instanceof Element ? event.target : null;
      while (node && node !== document.body) {
        if (node.matches('[role="dialog"], [role="menu"], input, textarea, select')) return;
        if (node.scrollHeight > node.clientHeight && /auto|scroll/.test(getComputedStyle(node).overflowY)) return;
        node = node.parentElement;
      }
      const rect = el.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      if (travel <= 0) return;
      const start = window.scrollY + rect.top;
      const offset = window.scrollY - start;
      // Scroll positions round to pixels, while layout bounds can remain fractional.
      const progress = Math.abs(offset) <= 1 ? 0
        : Math.abs(offset - travel) <= 1 ? 1 : offset / travel;
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
      const nextProgress = (window.scrollY + delta - start) / travel;
      const down = delta > 0;
      const approaching = down
        ? progress < 0 && nextProgress >= 0
        : progress > 1 && nextProgress <= 1;
      const inside = progress >= 0 && progress <= 1;
      if (!inside && !approaching) {
        gestureLocked = false;
        return;
      }
      // Release either endpoint after the turn without requiring a gesture gap.
      if (((down && progress >= 0.999) || (!down && progress <= 0.001)) && current === target) {
        gestureLocked = false;
        return;
      }
      const now = Date.now();
      const freshGesture = now - lastWheelAt >= 160
        || delta * lastDelta < 0;
      lastWheelAt = now;
      lastDelta = delta;
      // Absorb momentum only while the accepted step is animating, never indefinitely.
      if (gestureLocked && !freshGesture && current !== target) {
        event.preventDefault();
        return;
      }
      if ((down && progress >= 0.999) || (!down && progress <= 0.001)) return;
      const stop = approaching ? down ? 0 : 1 : down ? 1 : 0;
      event.preventDefault();
      gestureLocked = true;
      window.scrollTo({ top: start + travel * stop, behavior: 'auto' });
      measure();
    };
    window.addEventListener('scroll', measure, { passive: true, capture: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    if (isNarrow) {
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (stickyRef.current) observer?.observe(stickyRef.current);
    measure();
    return () => {
      selectOfferRef.current = null;
      observer?.disconnect();
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      if (snapTimer !== null) window.clearTimeout(snapTimer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [stacked, isNarrow]);

  const ramp = (a: number, b: number) => ease(clamp((p - a) / (b - a)));
  const cIn = 1;
  const cOut = ramp(0.24, 0.4);
  const aIn = ramp(0.4, 0.56);
  const cA = cIn * (1 - cOut);
  const aA = aIn;
  const active = p < 0.4 ? 0 : 3;
  const focus = Math.max(cA, aA);

  const blades = BLADE_COLORS.map((c, i) => {
    const emphasis = i === 0 ? cA : i === 3 ? aA : 0;
    return {
      c,
      t: `rotate(${i * 90}deg) translate(${(5 * emphasis).toFixed(2)}px, ${(-12 * emphasis).toFixed(2)}px) scale(${(1 + 0.16 * emphasis).toFixed(3)})`,
      o: lerp(1, 0.22, Math.max(0, focus - emphasis)),
    };
  });

  const rot = 70 + 90 * ramp(0.2, 0.6);
  const logoX = 18;
  const logoS = isMedium ? 'clamp(120px, 18vw, 180px)' : Math.round(Math.min(380, Math.max(280, vh - 220)));
  const showCard = isMedium || vh >= 680;
  const h3Size = isMedium ? 22 : showCard ? V.agentTaglineSize : V.agentTaglineCompactSize;
  const titleSize = isMedium ? 32 : showCard ? V.agentTitleSize : V.agentTitleCompactSize;

  const chips = [
    { label: 'Certificates', on: cA > 0.5, dot: V.brandBlue },
    { label: 'Accounting', on: aA > 0.5, dot: V.tealStrong },
  ];

  if (stacked) {
    return (
      <Box sx={{ padding: '72px 22px 48px', textAlign: 'center' }}>
        <svg viewBox="-50 -50 100 100" width={96} height={96} style={{ margin: '0 auto 18px', display: 'block' }}>
          {BLADE_COLORS.map((c, i) => (
            <g key={c} transform={`rotate(${i * 90})`}>
              <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={c} />
              <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
            </g>
          ))}
        </svg>
        <Eyebrow>Why MillOS</Eyebrow>
        <Box component="h2" sx={{ fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: V.fontSize2_1rem, lineHeight: V.lineHeight1_12, letterSpacing: V.letterSpacing_0_03em }}>
          {offerHeading}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '28px', textAlign: 'left' }}>
          <Box sx={{ background: C.mist, border: `1px solid ${C.glassBorder}`, borderRadius: V.borderRadius22px, padding: '22px' }}>
            <Box sx={{ fontFamily: F.wordmark, fontSize: V.fontSize13, fontWeight: V.fontWeight600, color: V.brandBlue }}>01 · Certificates agent</Box>
            <Box component="h3" sx={{ fontFamily: F.ui, fontSize: V.fontSize24, lineHeight: V.lineHeight1_15, mt: '8px' }}>{offers.certificates.pain}</Box>
            <Box component="p" sx={{ fontSize: V.fontSize15, lineHeight: V.lineHeight1_55, color: V.body, mt: '10px' }}>{offers.certificates.description}</Box>
            <Box component="p" sx={{ fontWeight: V.fontWeight600, color: V.brandBlue }}>{offers.certificates.outcome}</Box>
            <Box component={RouterLink} to="/compliance-centre" sx={{ color: V.brandBlue }}>Explore Certificates →</Box>
          </Box>
          <Box sx={{ background: C.mist, border: `1px solid ${C.glassBorder}`, borderRadius: V.borderRadius22px, padding: '22px' }}>
            <Box sx={{ fontFamily: F.wordmark, fontSize: V.fontSize13, fontWeight: V.fontWeight600, color: V.tealText }}>02 · Accounting agent</Box>
            <Box component="h3" sx={{ fontFamily: F.ui, fontSize: V.fontSize24, lineHeight: V.lineHeight1_15, mt: '8px' }}>{offers.accounting.pain}</Box>
            <Box component="p" sx={{ fontSize: V.fontSize15, lineHeight: V.lineHeight1_55, color: V.body, mt: '10px' }}>{offers.accounting.description}</Box>
            <Box component="p" sx={{ fontWeight: V.fontWeight600, color: V.tealText }}>{offers.accounting.outcome}</Box>
            <Box component={RouterLink} to="/accounting-centre" sx={{ color: V.tealText }}>Explore Accounting →</Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isNarrow) {
    return (
      <Box ref={outerRef} sx={{ position: 'relative', height: '240svh', fontFamily: F.body, color: C.navy, background: C.white }}>
        <Box ref={stickyRef} sx={{
          position: 'sticky', top: 80, minHeight: 'calc(100svh - 80px)',
          boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '16px 24px', gap: isUnfolded ? '8px' : '12px',
          '@media (max-height:700px)': { padding: '12px 20px' },
        }}>
          <Box sx={{ textAlign: 'center', flexShrink: 0, mt: isMedium ? '14px' : '6px' }}>
            <Eyebrow>Why MillOS</Eyebrow>
            <Box component="h2" sx={{ fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: isMedium ? 'clamp(1.65rem, 3.3vw, 2.2rem)' : 'clamp(1.5rem, 6vw, 2rem)', lineHeight: 1.12, mt: '8px', mb: 0 }}>
              {offerHeading}
            </Box>
          </Box>
          <Box component="svg" aria-hidden="true" viewBox="-65 -65 130 130"
            sx={{
              width: 'clamp(108.9px, 19.36svh, 169.4px)', height: 'clamp(108.9px, 19.36svh, 169.4px)',
              flexShrink: 0, overflow: 'visible',
              '@media (max-height:700px)': { width: 87.12, height: 87.12 },
              '@media (min-width:640px) and (max-width:1100px)': {
                width: 'clamp(130.68px, 23.232svh, 203.28px)', height: 'clamp(130.68px, 23.232svh, 203.28px)',
              },
              '@media (min-width:640px) and (max-width:1100px) and (max-height:700px)': { width: 104.544, height: 104.544 },
            }}>
            <g data-agent-mill style={{ transform: `rotate(${rot + 90}deg)`, transformOrigin: '0 0' }}>
              {blades.map((blade, index) => (
                <g key={index} style={{ transform: blade.t, opacity: blade.o }}>
                  <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={blade.c} />
                  <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
                </g>
              ))}
            </g>
          </Box>
          <Box sx={{ display: 'grid', width: '100%', maxWidth: isMedium ? 560 : 420, mt: '16px', flexShrink: 0,
            '@media (max-height:700px)': { mt: '12px' },
          }}>
            {(['certificates', 'accounting'] as const).map((key, index) => {
              const opacity = index === 0 ? cA : aA;
              const selected = opacity > 0.5;
              const color = index === 0 ? V.brandBlue : V.tealText;
              return (
                <Box key={key} aria-hidden={!selected} inert={!selected}
                  sx={{ gridArea: '1 / 1', opacity, transform: `translateY(${(1 - opacity) * -16}px)`, pointerEvents: selected ? 'auto' : 'none' }}>
                  <Box component="h3" sx={{ fontFamily: F.display, fontSize: 26, lineHeight: 1.1, color, m: 0 }}>
                    {index === 0 ? 'Certificates' : 'Accounting'}
                  </Box>
                  <Box component="p" sx={{ fontSize: 13, lineHeight: 1.4, color: V.body, mt: 1, mb: 1.5,
                    '@media (max-height:700px)': { mt: 0.5, mb: 1.5 },
                  }}>{offers[key].compactDescription}</Box>
                  <Box sx={{ background: C.mist, border: `1px solid ${C.hairline}`, borderRadius: '16px', p: isUnfolded ? '6px' : '10px', display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                    {isUnfolded ? <AgentMock accounting={index === 1} compact /> : index === 0 ? (
                      <>
                        {[
                          ['Gas Safety · Flat 2', 'Renewal booked ✓'],
                          ['EICR · Flat 5', 'Engineer confirmed ✓'],
                          ['EPC · Flat 7', 'Valid to 2031'],
                        ].map(([label, status]) => (
                          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 0.5, width: '100%', borderBottom: `1px solid ${C.hairline}`, '&:last-child': { borderBottom: 0 } }}>
                            <Box sx={{ fontSize: 13 }}>{label}</Box>
                            <Box sx={{ fontSize: 12, fontWeight: V.fontWeight600, color: V.tealText }}>{status}</Box>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', width: '100%', fontSize: 13 }}>
                          <Box>Monthly expenses</Box>
                          <Box sx={{ fontWeight: V.fontWeight600 }}>£3,480</Box>
                        </Box>
                        <Box component="svg" role="img" aria-label="Example monthly expenses: April £900, May £1,200, June £1,380" viewBox="0 0 280 100"
                          sx={{ display: 'block', width: '100%', height: 100, '@media (max-height:700px)': { height: 72 } }}>
                          {[{ month: 'Apr', amount: 900 }, { month: 'May', amount: 1200 }, { month: 'Jun', amount: 1380 }].map(({ month, amount }, i) => {
                            const height = amount / 1380 * 58;
                            const x = 42 + i * 82;
                            return (
                              <g key={month}>
                                <rect x={x} y={78 - height} width="32" height={height} rx="4" fill={V.tealStrong} />
                                <text x={x + 16} y={73 - height} textAnchor="middle" fill={C.navy} fontFamily={F.body} fontSize="10">£{amount.toLocaleString('en-GB')}</text>
                                <text x={x + 16} y="95" textAnchor="middle" fill={V.body} fontFamily={F.body} fontSize="11">{month}</text>
                              </g>
                            );
                          })}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 12 }}>
                          <Box>Quarterly figures</Box>
                          <Box sx={{ fontWeight: V.fontWeight600, color: V.tealText }}>Prepared ✓</Box>
                        </Box>
                      </>
                    )}
                  </Box>
                  <Box component={RouterLink} to={index === 0 ? '/compliance-centre' : '/accounting-centre'} sx={{ fontSize: 13, fontWeight: V.fontWeight600, color }}>
                    Explore {index === 0 ? 'Certificates' : 'Accounting'} →
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', flexShrink: 0, mt: 'auto' }}>
            {chips.map((chip, index) => (
              <Box key={chip.label} component="button" type="button" aria-pressed={chip.on}
                onClick={() => selectOfferRef.current?.(index === 0 ? CERTIFICATES_STOP : ACCOUNTING_STOP)}
                sx={{ font: 'inherit', fontSize: 13, border: `1px solid ${chip.on ? chip.dot : C.hairline}`, borderRadius: '24px', px: 2, py: 1, color: C.navy, background: chip.on ? C.mist : C.white, cursor: 'pointer' }}>
                {chip.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  const panelCard = (
    title: 'Agent 01' | 'Agent 02',
    color: string,
    tagline: React.ReactNode,
    description: string,
    stat: React.ReactNode,
    cardRows: React.ReactNode,
    x: number,
    s: number,
    o: number,
    pe: boolean
  ) => (
    <Box aria-hidden={!pe} inert={!pe} sx={{
      gridArea: isMedium ? '1 / 1 / 3 / 2' : '1 / 1', minWidth: 0,
      transform: isMedium ? 'none' : `translateX(${x}px) scale(${s})`,
      transformOrigin: '0 0', opacity: o, pointerEvents: pe ? 'auto' : 'none',
      ...(isMedium ? { display: 'grid', gridTemplateRows: 'subgrid' } : {}),
    }}>
      <Box>
        <Box sx={{ fontFamily: F.wordmark, fontSize: V.fontSize12, fontWeight: V.fontWeight600, letterSpacing: V.letterSpacing0_14em, textTransform: 'uppercase', color: V.secondary }}>{title}</Box>
        <Box sx={{ fontFamily: F.wordmark, fontWeight: V.fontWeight700, fontSize: titleSize, letterSpacing: V.letterSpacing_0_04em, lineHeight: V.lineHeight1, color, mt: '6px' }}>{title === 'Agent 01' ? 'Certificates' : 'Accounting'}</Box>
        <Box component="h3" sx={{ fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: h3Size, lineHeight: V.lineHeight1_1, letterSpacing: V.letterSpacing_0_03em, mt: isMedium ? '10px' : showCard ? '16px' : '10px', mb: isMedium ? 0 : undefined }}>{tagline}</Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: showCard ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr', gap: '16px', mt: isMedium ? 0 : showCard ? '22px' : '14px', alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box component="p" sx={{ m: 0, fontSize: isMedium ? 14 : V.fontSize15_5, lineHeight: V.lineHeight1_45, color: V.body }}>{description}</Box>
          <Box component="p" sx={{ m: 0, fontFamily: F.wordmark, fontSize: V.fontSize16, fontWeight: V.fontWeight700, color, lineHeight: V.lineHeight1_4 }}>{stat}</Box>
        </Box>
        {showCard && (
          <Box sx={{
            background: C.mist, border: `1px solid ${C.glassBorder}`, borderRadius: V.borderRadius20px,
            padding: isMedium ? '8px' : '14px', display: 'flex', flexDirection: 'column', gap: isMedium ? '6px' : '8px',
          }}>
            {cardRows}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box ref={outerRef} sx={{ position: 'relative', height: '240vh', fontFamily: F.body, color: C.navy, background: C.white }}>
      <Box sx={{ position: 'sticky', top: 0, height: isMedium ? 'auto' : '100vh', minHeight: isMedium ? '100svh' : 520, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: isMedium ? '88px 24px 8px' : '92px 40px 26px', boxSizing: 'border-box' }}>
        <Box sx={{ position: 'relative', top: isMedium ? 0 : '24px', mt: isMedium ? '12px' : 0, textAlign: 'center', flex: 'none' }}>
          <Eyebrow>Why MillOS</Eyebrow>
          <Box component="h2" sx={{ maxWidth: '60rem', mx: 'auto', mb: 0, fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: isMedium ? 'clamp(1.65rem, 3.3vw, 2.2rem)' : V.agentsHeadingSize, lineHeight: V.lineHeight1_1, letterSpacing: V.letterSpacing_0_035em, mt: isMedium ? '6px' : '10px' }}>
            {offerHeading}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', flex: 1, maxWidth: 1180, width: '100%', margin: '0 auto',
          ...(isMedium ? {
            display: 'grid', gridTemplateColumns: 'minmax(0, 0.65fr) minmax(0, 1fr) minmax(0, 1fr)',
            gridTemplateRows: 'auto 1fr', columnGap: '24px', rowGap: '16px',
            alignContent: 'center', alignItems: 'start', flex: 'none', my: 'auto', py: '10px',
          } : {}),
        }}>
          <Box sx={{ gridArea: isMedium ? '2 / 1' : undefined, alignSelf: isMedium ? 'center' : undefined, position: isMedium ? 'relative' : 'absolute', left: isMedium ? 'auto' : `calc(${logoX}% - 30px)`, top: isMedium ? 'auto' : 'calc(50% + 50px)', width: logoS, height: logoS, maxWidth: isMedium ? '100%' : undefined, mx: isMedium ? 'auto' : undefined, transform: isMedium ? 'none' : 'translate(-50%,-50%)' }}>
            <Box sx={{ position: 'absolute', inset: '-14%', borderRadius: '50%', background: `radial-gradient(circle,${active === 0 ? V.brandBlueAlpha22 : V.brandTealAlpha26},transparent 66%)`, opacity: focus }} />
            <svg viewBox="-50 -50 100 100" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'visible' }}>
              <g data-agent-mill style={{ transform: `rotate(${rot}deg)`, transformOrigin: '0 0' }}>
                {blades.map((b, i) => (
                  <g key={i} style={{ transform: b.t, opacity: b.o }}>
                    <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={b.c} />
                    <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
                  </g>
                ))}
              </g>
            </svg>
          </Box>

          <Box sx={{ gridArea: isMedium ? '1 / 2 / 3 / 4' : undefined, gridTemplateRows: isMedium ? 'subgrid' : undefined, position: isMedium ? 'relative' : 'absolute', left: isMedium ? 'auto' : '43%', right: 0, top: isMedium ? 'auto' : 'calc(50% + 50px)', transform: isMedium ? 'none' : 'translateY(-50%)', display: 'grid', minWidth: 0 }}>
          {panelCard(
            'Agent 01',
            V.brandBlue,
            offers.certificates.pain,
            offers.certificates.description,
            offers.certificates.outcome,
            <AgentMock accounting={false} compact={false} />,
            Math.round(lerp(-150, 0, cIn) + lerp(0, -60, cOut)),
            lerp(0.86, 1, cIn),
            cA,
            cA > 0.5
          )}

          {panelCard(
            'Agent 02',
            V.tealStrong,
            offers.accounting.pain,
            offers.accounting.description,
            offers.accounting.outcome,
            <AgentMock accounting compact={false} />,
            Math.round(lerp(-150, 0, aIn)),
            lerp(0.86, 1, aIn),
            aA,
            aA > 0.5
          )}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', flex: 'none', width: 440, maxWidth: '100%', height: isMedium ? 56 : 88, mx: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '60px',
          '@keyframes offerInfinityCurrent': { from: { offsetDistance: '0%' }, to: { offsetDistance: '100%' } },
        }}>
          <Box component="svg" aria-hidden="true" viewBox="0 0 440 100" preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            {Array.from({ length: INFINITY_DOTS }, (_, i) => (
              <circle
                key={i}
                r={i % 3 === 0 ? 1.5 : 1}
                fill={BLADE_COLORS[i % BLADE_COLORS.length]}
                opacity={0.3 + (i % 4) * 0.1}
                style={{
                  offsetPath: `path("${INFINITY_PATH}")`,
                  offsetDistance: `${i / INFINITY_DOTS * 50}%`,
                  offsetRotate: '0deg',
                  transform: `translate(${((i * 5) % 7) - 3}px, ${((i * 13) % 9) - 4}px)`,
                  animation: `offerInfinityCurrent ${INFINITY_DURATION}s linear ${-i / INFINITY_DOTS * INFINITY_DURATION / 2}s infinite`,
                }}
              />
            ))}
          </Box>
          {chips.map((c) => (
            <Box
              key={c.label}
              component="button"
              type="button"
              aria-pressed={c.on}
              onClick={() => selectOfferRef.current?.(c.label === 'Certificates' ? CERTIFICATES_STOP : ACCOUNTING_STOP)}
              sx={{
                display: 'flex',
                position: 'relative',
                cursor: 'pointer',
                alignItems: 'center',
                gap: '8px',
                borderRadius: V.borderRadius32px,
                padding: '8px 15px',
                fontFamily: F.wordmark,
                fontSize: V.fontSize13,
                fontWeight: V.fontWeight600,
                background: c.on ? C.navy : V.paper,
                color: c.on ? V.paper : C.navy,
                border: `1px solid ${c.on ? C.navy : C.glassBorder}`,
                transition: 'background .35s ease,color .35s ease',
              }}
            >
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: V.borderRadius9px, background: c.dot }} />
              {c.label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MillosAgentsScroll;
